import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import NoDataTable from "./noDataTable";
import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { tokens } from "../theme";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    backgroundColor: isDragging ? "rgba(236,29,35,0.16)" : "transparent",
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.06)",
    },
  };

  return (
    <tr
      ref={setNodeRef}
      style={{
        ...style,
        opacity: isDragging ? 0.5 : 1,
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
};

const Reorder = ({ items, onReorder, action }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isLightMode = theme.palette.mode === "light";
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((item) => item._id === active.id);
      const newIndex = items.findIndex((item) => item._id === over.id);
      const newOrder = arrayMove(items, oldIndex, newIndex);
      onReorder(newOrder);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <Box>
        {action && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 1.5,
            }}
          >
            {action}
          </Box>
        )}
        <TableContainer sx={{ maxWidth: 420 }}>
          <Table
            sx={{
              backgroundColor: isLightMode ? "#f7f7f7" : colors.primary[400],
              borderRadius: 2,
              tableLayout: "fixed",
            }}
          >
            <TableHead sx={{ backgroundColor: colors.primary[700] }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, width: 80 }}>{t("table.order")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("table.name")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <SortableContext
                items={items.map((item) => item._id)}
                strategy={verticalListSortingStrategy}
              >
                {items.length > 0 ? (
                  items.map((item, index) => (
                    <SortableItem key={item._id} id={item._id}>
                      <>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell sx={{ cursor: "grab" }}>{item.name}</TableCell>
                      </>
                    </SortableItem>
                  ))
                ) : (
                  <NoDataTable cols={2} />
                )}
              </SortableContext>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </DndContext>
  );
};
export default Reorder;
